/**
 * Painel de estruturação de squads — modelo de métricas e DADOS DE DEMONSTRAÇÃO.
 *
 * ATENÇÃO: nada aqui toca o banco. As avaliações são geradas em memória e o que o
 * líder preenche fica em localStorage. É material para decidir O QUE medir antes de
 * modelar tabela — quando os pilares estiverem fechados, isto vira Prisma + API.
 */

export const PERIODOS = ['2025-T2', '2025-T3', '2025-T4', '2026-T1', '2026-T2', '2026-T3'] as const;
export type Periodo = (typeof PERIODOS)[number];
export const PERIODO_ATUAL: Periodo = '2026-T3';

export interface SquadDemo {
  id: string;
  nome: string;
  regiao: string;
  lider: string;
  /** Squads reais do sistema (RJ/ES e MG) vs. hipóteses para comparação. */
  real: boolean;
}

export const SQUADS_DEMO: SquadDemo[] = [
  { id: 'rj-es', nome: 'RJ/ES', regiao: 'Rio de Janeiro / Espírito Santo', lider: 'A definir', real: true },
  { id: 'mg', nome: 'MG', regiao: 'Minas Gerais', lider: 'A definir', real: true },
  { id: 'sp', nome: 'SP', regiao: 'São Paulo', lider: 'A definir', real: false },
  { id: 'pr-sc', nome: 'PR/SC', regiao: 'Paraná / Santa Catarina', lider: 'A definir', real: false },
];

/** O que o líder do squad preenche a cada período. */
export interface AvaliacaoSquad {
  squadId: string;
  periodo: Periodo;
  // Capacidade
  headcount: number;
  alocacaoMedia: number; // % do tempo alocado em projeto
  ociosidade: number; // % do tempo sem frente definida
  horasExtrasMes: number; // média por pessoa
  // Entrega
  entregasConcluidas: number; // quantas entregas fecharam no período (vazão)
  entregasNoPrazo: number; // %
  retrabalho: number; // % das entregas que voltaram
  backlogItens: number; // fila em aberto — NÃO é vazão, não serve de divisor de custo
  // Custo
  budgetPlanejado: number;
  budgetConsumido: number;
  // Pessoas
  clima: number; // 0-10
  sobrecarga: number; // 0-10
  riscoSaida: number; // 0-10
  pessoasChave: number; // quantas pessoas seguram conhecimento crítico (bus factor)
  // Qualitativo
  gargalos: string;
  acaoProposta: string;
}

export type PilarId = 'capacidade' | 'entrega' | 'custo' | 'pessoas';

export interface Pilar {
  id: PilarId;
  nome: string;
  descricao: string;
  /** O que este pilar responde na conversa de reestruturação. */
  pergunta: string;
}

export const PILARES: Pilar[] = [
  {
    id: 'capacidade',
    nome: 'Capacidade',
    descricao: 'Alocação, ociosidade e horas extras',
    pergunta: 'O squad está do tamanho certo para a demanda que recebe?',
  },
  {
    id: 'entrega',
    nome: 'Entrega',
    descricao: 'Prazo, retrabalho e backlog por pessoa',
    pergunta: 'O problema é tamanho de time ou é processo?',
  },
  {
    id: 'custo',
    nome: 'Custo',
    descricao: 'Aderência ao budget planejado',
    pergunta: 'O squad se paga? Onde o dinheiro está furando?',
  },
  {
    id: 'pessoas',
    nome: 'Pessoas',
    descricao: 'Clima, sobrecarga, risco de saída e concentração de conhecimento',
    pergunta: 'Quem quebra se reestruturarmos sem olhar isso?',
  },
];

export type Pesos = Record<PilarId, number>;

export const PESOS_PADRAO: Pesos = { capacidade: 25, entrega: 30, custo: 25, pessoas: 20 };

const clamp = (n: number, min = 0, max = 100) => Math.min(max, Math.max(min, n));

/** Faixa saudável de alocação: nem ocioso, nem queimando o time. */
const ALOCACAO_IDEAL = 92;

export function scoreCapacidade(a: AvaliacaoSquad): number {
  const desvio = Math.abs(a.alocacaoMedia - ALOCACAO_IDEAL);
  const base = 100 - desvio * 1.5;
  const penalidadeOciosidade = a.ociosidade * 0.9;
  const penalidadeHoraExtra = Math.min(a.horasExtrasMes / 2, 12);
  return clamp(base - penalidadeOciosidade - penalidadeHoraExtra);
}

export function scoreEntrega(a: AvaliacaoSquad): number {
  const backlogPorPessoa = a.headcount > 0 ? a.backlogItens / a.headcount : 0;
  const penalidadeBacklog = clamp((backlogPorPessoa - 4) * 1.5, 0, 12);
  return clamp(a.entregasNoPrazo - a.retrabalho - penalidadeBacklog);
}

export function scoreCusto(a: AvaliacaoSquad): number {
  if (a.budgetPlanejado <= 0) return 50;
  const consumo = a.budgetConsumido / a.budgetPlanejado;
  const desvio = consumo - 1;
  // Estourar pesa mais que sobrar, mas sobrar muito também é erro de previsão.
  const penalidade = desvio > 0 ? desvio * 250 : Math.abs(desvio) * 80;
  return clamp(100 - penalidade);
}

export function scorePessoas(a: AvaliacaoSquad): number {
  // O piso em 30 é proposital: clima baixo derruba o pilar, mas não zera o squad
  // sozinho — senão sobrecarga e risco de saída deixam de ordenar os times.
  const base = 30 + a.clima * 7;
  const penalidadeConcentracao = clamp((3 - a.pessoasChave) * 5, 0, 12);
  return clamp(base - a.sobrecarga * 1.5 - a.riscoSaida * 1.7 - penalidadeConcentracao);
}

export type Scores = Record<PilarId, number>;

export function calcularScores(a: AvaliacaoSquad): Scores {
  return {
    capacidade: scoreCapacidade(a),
    entrega: scoreEntrega(a),
    custo: scoreCusto(a),
    pessoas: scorePessoas(a),
  };
}

/** Índice de Saúde do Squad: média ponderada dos pilares, 0–100. */
export function calcularIndice(scores: Scores, pesos: Pesos): number {
  const total = PILARES.reduce((soma, p) => soma + pesos[p.id], 0);
  if (total <= 0) return 0;
  const somaPonderada = PILARES.reduce((soma, p) => soma + scores[p.id] * pesos[p.id], 0);
  return somaPonderada / total;
}

export type FaixaId = 'saudavel' | 'atencao' | 'serio' | 'critico';

export interface Faixa {
  id: FaixaId;
  rotulo: string;
  minimo: number;
  /** Token de status — nunca reaproveitado como cor de série. */
  cor: string;
}

export const FAIXAS: Faixa[] = [
  { id: 'saudavel', rotulo: 'Saudável', minimo: 75, cor: '#0ca30c' },
  { id: 'atencao', rotulo: 'Atenção', minimo: 60, cor: '#fab219' },
  { id: 'serio', rotulo: 'Preocupante', minimo: 45, cor: '#ec835a' },
  { id: 'critico', rotulo: 'Crítico', minimo: -Infinity, cor: '#d03b3b' },
];

export function faixaDoIndice(indice: number): Faixa {
  return FAIXAS.find((f) => indice >= f.minimo) ?? FAIXAS[FAIXAS.length - 1];
}

// ---------------------------------------------------------------------------
// Geração dos dados de demonstração
// ---------------------------------------------------------------------------

/** Hash determinístico (FNV-1a) → 0..1, para o jitter não mudar a cada render. */
function ruido(semente: string): number {
  let h = 2166136261;
  for (let i = 0; i < semente.length; i++) {
    h ^= semente.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}

/** Cenário-base de cada squad no período atual. Cada um conta uma história diferente. */
const BASE_ATUAL: Record<string, Omit<AvaliacaoSquad, 'squadId' | 'periodo'>> = {
  // Time grande, absorvendo demanda demais, budget estourado.
  'rj-es': {
    headcount: 14,
    alocacaoMedia: 118,
    ociosidade: 3,
    horasExtrasMes: 22,
    entregasConcluidas: 32,
    entregasNoPrazo: 71,
    retrabalho: 14,
    backlogItens: 68,
    budgetPlanejado: 1_450_000,
    budgetConsumido: 1_692_000,
    clima: 6.2,
    sobrecarga: 8.4,
    riscoSaida: 6.5,
    pessoasChave: 2,
    gargalos:
      'O squad absorve demanda de dois clientes grandes com a mesma equipe. Duas pessoas concentram todo o conhecimento de apreciação de risco e viram gargalo em toda revisão.',
    acaoProposta:
      'Separar a frente do segundo cliente ou repassar para MG, que está com folga. Documentar o fluxo de revisão para tirar a dependência das duas pessoas-chave.',
  },
  // Time pequeno e ocioso, entrega bem o pouco que pega, sobra budget.
  mg: {
    headcount: 6,
    alocacaoMedia: 64,
    ociosidade: 28,
    horasExtrasMes: 2,
    entregasConcluidas: 12,
    entregasNoPrazo: 89,
    retrabalho: 5,
    backlogItens: 11,
    budgetPlanejado: 780_000,
    budgetConsumido: 604_000,
    clima: 7.4,
    sobrecarga: 3.1,
    riscoSaida: 4.0,
    pessoasChave: 4,
    gargalos:
      'Falta demanda alocada, não falta gente. O time entrega bem, mas passa boa parte do trimestre sem frente definida e a equipe começa a questionar a própria utilidade.',
    acaoProposta:
      'Puxar a frente excedente de RJ/ES para cá antes de discutir corte de headcount. A ociosidade é problema de distribuição de demanda, não de produtividade.',
  },
  // O caso crítico: entrega ruim, retrabalho alto, clima deteriorado.
  sp: {
    headcount: 9,
    alocacaoMedia: 103,
    ociosidade: 6,
    horasExtrasMes: 14,
    entregasConcluidas: 15,
    entregasNoPrazo: 52,
    retrabalho: 27,
    backlogItens: 74,
    budgetPlanejado: 1_100_000,
    budgetConsumido: 1_268_000,
    clima: 4.1,
    sobrecarga: 7.8,
    riscoSaida: 8.2,
    pessoasChave: 1,
    gargalos:
      'Mais de um quarto das entregas volta para correção, o que gera retrabalho e joga o prazo para frente. O clima caiu junto e duas pessoas já sinalizaram saída.',
    acaoProposta:
      'O problema aqui é processo, não tamanho: cortar gente pioraria. Revisar o critério de aceite e emparelhar com PR/SC por um trimestre.',
  },
  // A referência: o squad que funciona.
  'pr-sc': {
    headcount: 8,
    alocacaoMedia: 94,
    ociosidade: 5,
    horasExtrasMes: 4,
    entregasConcluidas: 26,
    entregasNoPrazo: 91,
    retrabalho: 6,
    backlogItens: 22,
    budgetPlanejado: 900_000,
    budgetConsumido: 878_000,
    clima: 8.1,
    sobrecarga: 3.4,
    riscoSaida: 2.6,
    pessoasChave: 5,
    gargalos:
      'Sem gargalo relevante no trimestre. O time roda com previsibilidade e o conhecimento está distribuído.',
    acaoProposta:
      'Usar como referência de processo para os demais squads em vez de mexer na estrutura.',
  },
};

/**
 * Gera a avaliação de um squad em um período.
 *
 * Períodos anteriores são reconstruídos a partir do cenário atual aplicando uma
 * deriva: quanto mais antigo, melhor estava — é a curva de deterioração que motiva
 * a conversa de reestruturação.
 */
export function gerarAvaliacao(squadId: string, periodo: Periodo): AvaliacaoSquad {
  const base = BASE_ATUAL[squadId] ?? BASE_ATUAL['pr-sc'];
  const idx = PERIODOS.indexOf(periodo);
  const distancia = PERIODOS.length - 1 - idx; // 0 = período atual
  const j = (chave: string, amplitude: number) => (ruido(`${squadId}:${periodo}:${chave}`) - 0.5) * amplitude;

  const sobrecargaExtra = base.alocacaoMedia > 100 ? distancia * 2.4 : -distancia * 1.6;

  return {
    squadId,
    periodo,
    headcount: base.headcount,
    alocacaoMedia: Math.round(clamp(base.alocacaoMedia - sobrecargaExtra + j('al', 4), 30, 145)),
    ociosidade: Math.round(clamp(base.ociosidade - distancia * 1.1 + j('oc', 3), 0, 60)),
    horasExtrasMes: Math.round(clamp(base.horasExtrasMes - distancia * 1.8 + j('he', 3), 0, 60)),
    entregasConcluidas: Math.round(clamp(base.entregasConcluidas + distancia * 1.4 + j('ec', 3), 1, 200)),
    entregasNoPrazo: Math.round(clamp(base.entregasNoPrazo + distancia * 2.6 + j('ep', 5), 0, 100)),
    retrabalho: Math.round(clamp(base.retrabalho - distancia * 1.4 + j('rt', 3), 0, 60)),
    backlogItens: Math.round(clamp(base.backlogItens - distancia * 3.5 + j('bl', 6), 0, 200)),
    budgetPlanejado: base.budgetPlanejado,
    budgetConsumido: Math.round(base.budgetConsumido * (1 - distancia * 0.022 + j('bc', 0.02))),
    clima: Number(clamp(base.clima + distancia * 0.34 + j('cl', 0.5), 0, 10).toFixed(1)),
    sobrecarga: Number(clamp(base.sobrecarga - distancia * 0.3 + j('sb', 0.5), 0, 10).toFixed(1)),
    riscoSaida: Number(clamp(base.riscoSaida - distancia * 0.42 + j('rs', 0.5), 0, 10).toFixed(1)),
    pessoasChave: base.pessoasChave,
    gargalos: base.gargalos,
    acaoProposta: base.acaoProposta,
  };
}

// ---------------------------------------------------------------------------
// Persistência local (enquanto não há banco)
// ---------------------------------------------------------------------------

const CHAVE_AVALIACOES = 'squad-painel:avaliacoes';
const CHAVE_DECISOES = 'squad-painel:decisoes';
const CHAVE_PESOS = 'squad-painel:pesos';

function lerJson<T>(chave: string, padrao: T): T {
  try {
    const bruto = localStorage.getItem(chave);
    return bruto ? (JSON.parse(bruto) as T) : padrao;
  } catch {
    return padrao;
  }
}

function gravarJson(chave: string, valor: unknown): void {
  try {
    localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    // Sem localStorage (aba anônima, quota): o painel segue com o mock em memória.
  }
}

type MapaAvaliacoes = Record<string, AvaliacaoSquad>;

const chaveDe = (squadId: string, periodo: Periodo) => `${squadId}|${periodo}`;

/** Avaliação salva pelo líder, se houver; senão o cenário de demonstração. */
export function obterAvaliacao(squadId: string, periodo: Periodo): AvaliacaoSquad {
  const salvas = lerJson<MapaAvaliacoes>(CHAVE_AVALIACOES, {});
  return salvas[chaveDe(squadId, periodo)] ?? gerarAvaliacao(squadId, periodo);
}

export function salvarAvaliacao(avaliacao: AvaliacaoSquad): void {
  const salvas = lerJson<MapaAvaliacoes>(CHAVE_AVALIACOES, {});
  salvas[chaveDe(avaliacao.squadId, avaliacao.periodo)] = avaliacao;
  gravarJson(CHAVE_AVALIACOES, salvas);
}

export function foiPreenchidaPeloLider(squadId: string, periodo: Periodo): boolean {
  const salvas = lerJson<MapaAvaliacoes>(CHAVE_AVALIACOES, {});
  return Boolean(salvas[chaveDe(squadId, periodo)]);
}

export function descartarAvaliacao(squadId: string, periodo: Periodo): void {
  const salvas = lerJson<MapaAvaliacoes>(CHAVE_AVALIACOES, {});
  delete salvas[chaveDe(squadId, periodo)];
  gravarJson(CHAVE_AVALIACOES, salvas);
}

/** Decisão do gestor sobre cada painel: o que fica no sistema e o que é descartado. */
export type Decisao = 'indefinido' | 'manter' | 'cortar';

export function obterDecisoes(): Record<string, Decisao> {
  return lerJson<Record<string, Decisao>>(CHAVE_DECISOES, {});
}

export function salvarDecisao(painelId: string, decisao: Decisao): Record<string, Decisao> {
  const atuais = obterDecisoes();
  const proximas = { ...atuais, [painelId]: decisao };
  gravarJson(CHAVE_DECISOES, proximas);
  return proximas;
}

export function obterPesos(): Pesos {
  return lerJson<Pesos>(CHAVE_PESOS, PESOS_PADRAO);
}

export function salvarPesos(pesos: Pesos): void {
  gravarJson(CHAVE_PESOS, pesos);
}

// ---------------------------------------------------------------------------
// Visão agregada
// ---------------------------------------------------------------------------

export interface VisaoSquad {
  squad: SquadDemo;
  avaliacao: AvaliacaoSquad;
  scores: Scores;
  indice: number;
  faixa: Faixa;
  preenchidaPeloLider: boolean;
  /** Desvio da alocação em pontos percentuais sobre a faixa saudável (100%). */
  desvioAlocacao: number;
  desvioBudget: number;
  custoPorEntrega: number;
}

export function montarVisao(periodo: Periodo, pesos: Pesos): VisaoSquad[] {
  return SQUADS_DEMO.map((squad) => {
    const avaliacao = obterAvaliacao(squad.id, periodo);
    const scores = calcularScores(avaliacao);
    const indice = calcularIndice(scores, pesos);
    // Divisor é a vazão de fato, nunca o backlog: fila em aberto não é entrega feita.
    const entregasEstimadas = Math.max(1, avaliacao.entregasConcluidas);
    return {
      squad,
      avaliacao,
      scores,
      indice,
      faixa: faixaDoIndice(indice),
      preenchidaPeloLider: foiPreenchidaPeloLider(squad.id, periodo),
      desvioAlocacao: avaliacao.alocacaoMedia - 100,
      desvioBudget: avaliacao.budgetConsumido - avaliacao.budgetPlanejado,
      custoPorEntrega: avaliacao.budgetConsumido / entregasEstimadas,
    };
  });
}

export function montarHistorico(pesos: Pesos): Array<Record<string, string | number>> {
  return PERIODOS.map((periodo) => {
    const linha: Record<string, string | number> = { periodo };
    SQUADS_DEMO.forEach((squad) => {
      const avaliacao = obterAvaliacao(squad.id, periodo);
      linha[squad.id] = Number(calcularIndice(calcularScores(avaliacao), pesos).toFixed(1));
    });
    return linha;
  });
}

export const formatarMoeda = (valor: number): string =>
  valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

export const formatarMoedaCompacta = (valor: number): string =>
  `R$ ${(valor / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}M`;
